import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatDashboardUserError } from "../utils/formatDashboardUserError";
import { useReportDashboardShareReady } from "../context/DashboardShareReadinessContext";

/**
 * Estado normalizado para abas do dashboard (loading, preview, deferred, erros).
 * Aceita o retorno de useDashboard ou useSurveyAnalytics.
 */
export function useDashboardTabState(queryResult = {}, options = {}) {
  const { t } = useTranslation("settings");
  const registerShare = options.registerShare !== false;

  const isDeferred = Boolean(queryResult.isDeferred ?? queryResult.isComputing);
  const isPreview = Boolean(queryResult.isPreview);
  const isBackgroundJob = Boolean(queryResult.isBackgroundJob);
  const isAwaitingPreview = Boolean(queryResult.isAwaitingPreview);
  const isInitialLoading = Boolean(queryResult.isLoading);
  const isFetchingFull = Boolean(queryResult.isFetchingFull);
  const userCancelled = Boolean(queryResult.userCancelled);
  const hasData = queryResult.data != null && queryResult.data !== undefined;

  const errorMessage = useMemo(() => {
    if (!queryResult.error || isDeferred || userCancelled) return null;
    return formatDashboardUserError(queryResult.error, t);
  }, [queryResult.error, isDeferred, userCancelled, t]);

  const isShareReady =
    hasData && !isPreview && !isDeferred && !isInitialLoading && !isFetchingFull && !errorMessage;

  useReportDashboardShareReady(registerShare && isShareReady);

  const showSkeletons =
    !hasData &&
    !errorMessage &&
    !userCancelled &&
    !isDeferred &&
    (isInitialLoading ||
      isAwaitingPreview ||
      Boolean(queryResult.isFetching && !isBackgroundJob));

  const showContent =
    !showSkeletons &&
    !errorMessage &&
    (hasData || (!isBackgroundJob && !isDeferred && !isAwaitingPreview));

  return {
    isDeferred,
    isPreview,
    isBackgroundJob,
    isAwaitingPreview,
    isInitialLoading,
    isFetchingFull,
    isShareReady,
    hasData,
    errorMessage,
    showContent,
    showSkeletons,
    data: queryResult.data,
  };
}
